import { createFileRoute } from '@tanstack/react-router';
import { useMutationLogin } from '@/api/queries/auth';
import useAuthStore from '@/store/auth';
import apiConfig from '@/config/url-api';
import { useRouter } from 'next/router';
import Head from 'next/head';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { resetSessionExpired } from '@/api/axios/handleUnauthorized';
import { motion, type Variants, AnimatePresence } from 'framer-motion';
import { cn } from '@/utils';
import {
  ArrowRight,
  ArrowUpRight,
  Satellite,
  Radio,
  Sun,
  Gauge,
  Globe2,
  ShieldCheck,
  Play,
  Star,
  Download,
  Award,
} from 'lucide-react';
import ShipLoading from '@/components/ui/loader/ShipLoading';
import { LandingTopBar } from '@/featured/landing-page/components/LandingTopBar';
import { LandingFooter } from '@/featured/landing-page/components/LandingFooter';
import ModalVideo from 'react-modal-video';
import 'react-modal-video/css/modal-video.css';
const VideoModal: any = (ModalVideo as any)?.default || ModalVideo;
import { landingPageServiceData, clientsData } from '@/assets/data/landing-page';

const HERO_TAGLINES = [
  "Vessel Tracking System",
  "Real-Time Vessel Tracking Across the Ocean",
  "Stay Connected Beyond the Horizon",
  "Monitor Your Fleet with Complete Confidence",
  "Maritime Visibility Without Limits",
  "Reliable Tracking for Every Voyage",
  "Know Where Your Vessels Are, Instantly",
  "Advanced GPS Solutions for Maritime Operations",
  "Smarter Fleet Monitoring Starts Here",
  "From Port to Open Sea, Always Connected",
  "Tracking Technology Built for the Maritime Industry",
];

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const stagger: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

const Home = () => {
  const authStore = useAuthStore((state) => state);
  const authMutation = useMutationLogin();
  const router = useRouter();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      document.documentElement.setAttribute('dir', 'ltr');
    }
  }, []);

  const onDemoClick = async () => {
    try {
      const loginData = await authMutation.mutateAsync({
        email: 'trial@missi.id',
        password: 'qwerty123',
      });
      resetSessionExpired();
      localStorage.setItem(apiConfig.auth.storageTokenKeyName, loginData.token);
      localStorage.setItem(apiConfig.auth.storageUserKeyName, JSON.stringify(loginData.user));
      authStore.setLogin(loginData?.user);
      router.push('/apps');
    } catch (e) {
      toast.error('Failed to access demo, please try again');
    }
  };

  return (
    <>
      <Head>
        <title>Missi Track — Sistem Pelacakan &amp; Monitoring Kapal Maritim Real-Time</title>
        <meta
          name="description"
          content="Missi Track menghadirkan sistem pelacakan kapal GPS &amp; AIS real-time, telemetri satelit IoT, dan monitoring armada maritim di Indonesia dan mancanegara."
        />
        <meta
          name="keywords"
          content="vessel tracking system, pelacakan kapal, monitoring kapal laut, AIS maritim, tracking armada kapal, GPS kapal laut, missi track, fleet management maritim indonesia"
        />
        <link rel="canonical" href="https://v2.missi-track.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://v2.missi-track.com/" />
        <meta property="og:site_name" content="MISSI Track" />
        <meta property="og:locale" content="id_ID" />
        <meta property="og:title" content="Missi Track — Sistem Pelacakan &amp; Monitoring Kapal Maritim Real-Time" />
        <meta
          property="og:description"
          content="Sistem monitoring dan pelacakan kapal real-time berbasis AIS &amp; satelit IoT untuk armada maritim Indonesia. Pantau posisi, rute, dan performa armada secara akurat."
        />
        <meta property="og:image" content="https://v2.missi-track.com/images/laptop.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Preview Dashboard Platform Monitoring Kapal Missi Track" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@missitrack" />
        <meta name="twitter:title" content="Missi Track — Sistem Pelacakan &amp; Monitoring Kapal Maritim Real-Time" />
        <meta
          name="twitter:description"
          content="Platform monitoring dan pelacakan kapal maritim real-time berbasis AIS, GPS, dan IoT satelit."
        />
        <meta name="twitter:image" content="https://v2.missi-track.com/images/laptop.png" />
      </Head>

      {authMutation.isPending && <ShipLoading />}

      <main className="landing min-h-screen overflow-hidden">
        <LandingTopBar />
        <Hero onDemoClick={onDemoClick} />
        <ServicesGrid />
        <WhyMissi />
        <AchievementSection />
        <Impact />
        <Reviews />
        <CTASection />
        <LandingFooter />
      </main>
    </>
  );
};

/* ---------------- Hero ---------------- */
function Hero({ onDemoClick }: { onDemoClick: () => void }) {
  const [isOpen, setOpen] = useState(false);
  return (
    <section className="relative -mt-24 flex min-h-[100svh] items-center overflow-hidden px-4 pt-32 pb-16 md:pb-24">
      {/* Background video */}
      <video
        src="/videos/hero-video.mp4"
        autoPlay
        loop
        muted
        playsInline
        aria-hidden="true"
        poster="/images/fleet-ocean.jpg"
        width={1920}
        height={1080}
        className="absolute inset-0 z-0 h-full w-full object-cover"
        style={{
          filter: 'blur(2px) brightness(0.45)',
          transform: 'scale(1.05)',
        }}
      />
      {/* Cinematic gradient overlay */}
      <div
        className="absolute inset-0 z-10"
        style={{
          background:
            'linear-gradient(180deg, rgba(11, 15, 25, 0.78) 0%, rgba(11, 15, 25, 0.45) 40%, rgba(11, 15, 25, 0.85) 100%)',
        }}
      />
      {/* Subtle radial accent */}
      <div
        aria-hidden
        className="absolute inset-0 z-10"
        style={{
          background:
            'radial-gradient(60% 50% at 50% 35%, rgba(0, 150, 251, 0.25), transparent 70%)',
        }}
      />

      <div className="relative z-20 mx-auto w-full max-w-6xl">
        <motion.div
          initial="hidden"
          animate="show"
          variants={stagger}
          className="flex flex-col items-center text-center"
        >
          {/* Eyebrow */}
          <motion.span
            variants={fadeUp}
            className="inline-flex items-center space-x-2 rounded-full border px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-white"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              borderColor: 'rgba(255, 255, 255, 0.25)',
            }}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_12px_var(--primary)]" />
            <span>Missi Track</span>
          </motion.span>

          {/* Rotating headline */}
          <motion.div variants={fadeUp} className="mt-8 w-full">
            <RotatingHeadline />
          </motion.div>

          {/* Subtitle */}
          <motion.p
            variants={fadeUp}
            className="mt-10 max-w-2xl text-balance text-base leading-relaxed text-slate-300 md:mt-14 lg:mt-16 md:text-lg"
          >
            Welcome to Missi Track Vessel Tracking, where we assist you in efficiently managing and tracking your vessels. Our mission is to provide seamless support for your maritime operations, ensuring optimal control and oversight of your fleet.
          </motion.p>

          {/* CTAs */}
          <motion.div
            variants={fadeUp}
            className="mt-10 flex flex-col flex-wrap items-center justify-center gap-3.5 sm:flex-row"
          >
            <a
              href="/contact"
              className="group inline-flex h-12 items-center justify-center space-x-2 rounded-full bg-primary px-7 text-sm font-semibold text-primary-foreground shadow-[0_10px_40px_-10px_var(--primary-color)] transition hover:scale-[1.02]"
            >
              <span>Request a demo</span>
              <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
            </a>
            <button
              onClick={() => setOpen(true)}
              className="inline-flex h-12 items-center justify-center space-x-2 rounded-full border border-white/30 bg-white/12 px-7 text-sm font-semibold text-white transition hover:bg-white/20"
            >
              <Play className="h-4 w-4 fill-current" />
              <span>See how it works</span>
            </button>
            <a
              href="https://play.google.com/store/apps/details?id=com.missi.app"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-12 items-center justify-center gap-2.5 rounded-full border border-white/30 bg-slate-950/75 px-6 text-white shadow-lg backdrop-blur-md transition hover:scale-[1.02] hover:border-white/50 hover:bg-slate-900"
            >
              <svg className="h-5 w-5 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M3.61 1.814L13.793 12 3.61 22.186a1.75 1.75 0 01-.473-1.206V3.02c0-.465.174-.895.473-1.206z" />
                <path fill="#34A853" d="M17.218 8.575L13.793 12 3.61 1.814c.385-.401.988-.52 1.547-.202l12.061 6.963z" />
                <path fill="#FBBC05" d="M20.892 10.696c.875.505.875 2.103 0 2.608l-3.674 2.121L13.793 12l3.425-3.425 3.674 2.121z" />
                <path fill="#EA4335" d="M17.218 15.425L5.157 22.388c-.559.318-1.162.199-1.547-.202L13.793 12l3.425 3.425z" />
              </svg>
              <div className="flex flex-col items-start leading-none text-left">
                <span className="text-[9px] font-medium uppercase tracking-wider text-slate-300">Get it on</span>
                <span className="text-xs font-bold tracking-tight text-white mt-0.5">Google Play</span>
              </div>
            </a>
          </motion.div>
          {isOpen && typeof window !== 'undefined' && (
            <VideoModal
              channel="youtube"
              autoplay
              isOpen={isOpen}
              videoId="SKdPLZyIN0o"
              onClose={() => setOpen(false)}
            />
          )}

          {/* Glass stats strip */}
          <motion.div
            variants={fadeUp}
            className="mt-16 grid w-full max-w-3xl grid-cols-2 gap-px overflow-hidden rounded-2xl border md:grid-cols-4"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              borderColor: 'rgba(255, 255, 255, 0.2)',
            }}
          >
            {[
              { n: '600++', l: 'Tracking Active' },
              { n: '50++', l: 'Companies' },
              { n: '99%', l: 'Uptime' },
              { n: '7 yrs', l: 'Experience' },
            ].map((s) => (
              <div
                key={s.l}
                className="flex flex-col items-center justify-center space-y-1 px-4 py-5"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                }}
              >
                <div className="text-2xl font-black text-white md:text-3xl">{s.n}</div>
                <div className="text-[11px] uppercase tracking-wider text-white/60">{s.l}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        aria-hidden
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2"
      >
        <div className="flex h-9 w-6 items-start justify-center rounded-full border border-white/30 p-1.5">
          <motion.span
            className="h-2 w-1 rounded-full bg-white/80"
            animate={{ y: [0, 10, 0], opacity: [1, 0.2, 1] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
          />
        </div>
      </motion.div>
    </section>
  );
}

function RotatingHeadline() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((p) => (p + 1) % HERO_TAGLINES.length), 3600);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="relative flex min-h-[7rem] items-center justify-center sm:min-h-[9rem] md:min-h-[12rem] lg:min-h-[14rem]">
      <AnimatePresence mode="wait">
        <motion.h1
          key={i}
          initial={{ opacity: 0, y: 28, filter: 'blur(10px)' }}
          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          exit={{ opacity: 0, y: -20, filter: 'blur(10px)' }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="display absolute mx-auto max-w-5xl text-balance text-center font-black text-white px-2"
          style={{
            fontSize: 'clamp(2rem, 5.5vw, 5rem)',
            lineHeight: 1.08,
            textShadow: '0 4px 30px oklch(0.1 0.04 240 / 0.6)',
          }}
        >
          {HERO_TAGLINES[i]}
        </motion.h1>
      </AnimatePresence>
    </div>
  );
}

/* ---------------- Services ---------------- */
function ServicesGrid() {
  return (
    <section id="services" className="px-4 py-24 bg-background">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-80px' }}
          variants={stagger}
          className="mb-12 flex flex-wrap items-end justify-between gap-6"
        >
          <motion.div variants={fadeUp} className="max-w-2xl">
            <span className="pill text-primary">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" /> What we do
            </span>
            <h2 className="mt-5 text-5xl font-black md:text-7xl">
              FIND YOUR FLEET,
              <br />
              <span className="text-primary">TRACK IT</span> TODAY
            </h2>
          </motion.div>
          <motion.p variants={fadeUp} className="max-w-sm text-muted-foreground">
            From web-based GPS dashboards to solar satellite terminals — every service you need to
            keep eyes on every vessel.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-60px' }}
          variants={stagger}
          className="grid gap-5 md:grid-cols-2 lg:grid-cols-3"
        >
          {landingPageServiceData.map((s) => (
            <motion.div
              key={s.title}
              variants={fadeUp}
              className="group card-surface relative overflow-hidden p-6 hover-lift flex flex-col justify-between"
            >
              <div>
                <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 p-2.5 transition duration-300 group-hover:bg-primary/20">
                  <div className="relative w-full h-full flex items-center justify-center [&_img]:object-contain [&_img]:w-auto [&_img]:h-auto [&_img]:max-w-full [&_img]:max-h-full">
                    {s.image}
                  </div>
                </div>
                <h3 className="text-xl font-semibold">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.description}</p>
                {s.downloadUrl && (
                  <a
                    href={s.downloadUrl}
                    download
                    className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-primary transition hover:text-primary/80"
                  >
                    <Download className="h-3.5 w-3.5" />
                    Download Brochure (PDF)
                  </a>
                )}
              </div>
              {s.downloadUrl ? (
                <a
                  href={s.downloadUrl}
                  download
                  title="Download Brochure"
                  className="absolute right-5 top-5 h-8 w-8 flex items-center justify-center rounded-full bg-secondary/80 text-muted-foreground transition hover:bg-primary hover:text-primary-foreground z-10"
                >
                  <Download className="h-4 w-4" />
                </a>
              ) : (
                <ArrowUpRight className="absolute right-5 top-5 h-5 w-5 text-muted-foreground transition group-hover:text-primary" />
              )}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- Why Missi ---------------- */
function WhyMissi() {
  return (
    <section id="about" className="px-4 pb-24 bg-background">
      <div className="mx-auto max-w-7xl rounded-[2rem] bg-secondary/60 p-6 md:p-12">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={stagger}
          className="grid items-center gap-10 lg:grid-cols-2"
        >
          <motion.div variants={fadeUp}>
            <span className="pill text-primary">WHY MISSI TRACK?</span>
            <h2 className="mt-5 text-4xl font-black md:text-6xl">
              Strategic partner of the maritime industry in Indonesia
            </h2>
            <p className="mt-6 max-w-lg text-muted-foreground">
              Our philosophy is simple — customers come first. Big enough to perform, small enough to
              care. Award-winning solutions in tracking, communication, navigation and trading for
              fleets of every size.
            </p>

            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {[
                {
                  t: 'Award winning',
                  d: 'Indonesia International Business Award 2023 — Best in Business Excellence.',
                },
                {
                  t: 'Authorized supplier',
                  d: 'Skywave Orbcomm, Globalstar and Iridium Edge Solar products in Indonesia.',
                },
              ].map((f) => (
                <div key={f.t} className="card-surface p-5 hover-lift">
                  <h4 className="font-semibold">{f.t}</h4>
                  <p className="mt-1 text-sm text-muted-foreground">{f.d}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div variants={fadeUp} className="relative">
            <img
              src="/images/fleet-ocean.jpg"
              alt="Aerial view of vessels being tracked across the ocean"
              loading="lazy"
              width={1200}
              height={800}
              className="h-[420px] w-full rounded-[1.75rem] object-cover"
            />
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="absolute -bottom-6 left-6 right-6 grid grid-cols-3 gap-3 rounded-2xl bg-white p-5 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)] md:left-auto md:right-6 md:w-80"
            >
              <Stat n="600++" l="Tracking Active" />
              <Stat n="50++" l="Companies" />
              <Stat n="7" l="Years" />
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function Stat({ n, l }: { n: string; l: string }) {
  return (
    <div>
      <div className="text-2xl font-bold text-primary">{n}</div>
      <div className="text-xs text-muted-foreground">{l}</div>
    </div>
  );
}

/* ---------------- Achievement / Award ---------------- */
function AchievementSection() {
  const [isOpen, setOpen] = useState(false);
  return (
    <section className="px-4 pb-24 bg-background">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-60px' }}
          variants={stagger}
          className="grid items-center gap-10 lg:grid-cols-12 card-surface p-8 md:p-14 overflow-hidden relative"
        >
          {/* Image side */}
          <motion.div variants={fadeUp} className="relative lg:col-span-6 group overflow-hidden rounded-2xl">
            <img
              src="/images/winner.jpg"
              alt="Indonesia International Business Award 2023 Winner"
              loading="lazy"
              width={900}
              height={650}
              className="h-[380px] md:h-[450px] w-full object-cover rounded-2xl transition duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 flex items-center justify-between text-white">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-white/70">Recognition</div>
                <div className="text-lg font-bold text-white">Indonesia International Business Award 2023</div>
              </div>
              <button
                onClick={() => setOpen(true)}
                className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground shadow-lg transition hover:scale-110"
                title="Watch Award Video"
              >
                <Play className="h-5 w-5 translate-x-0.5 fill-current" />
              </button>
            </div>
          </motion.div>

          {/* Text side */}
          <motion.div variants={fadeUp} className="lg:col-span-6 lg:pl-6">
            <span className="pill text-primary">OUR ACHIEVEMENT</span>
            <h2 className="mt-5 text-4xl font-black md:text-6xl text-foreground">
              BEST IN BUSINESS <span className="text-primary">EXCELLENCE WINNER</span>
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Indonesia International Business Award 2023 — Recognized as the leading provider of electronic solutions and satellite tracking innovation for shipowners across Indonesia and international waters.
            </p>

            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              <div className="flex items-start gap-3.5 rounded-xl border p-4 bg-background/50">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                  <Award className="h-5 w-5" />
                </span>
                <div>
                  <div className="font-bold text-foreground text-sm">Award Winning Formula</div>
                  <div className="text-xs text-muted-foreground mt-0.5">1st place formula for maritime visibility & tracking</div>
                </div>
              </div>

              <div className="flex items-start gap-3.5 rounded-xl border p-4 bg-background/50">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                  <ShieldCheck className="h-5 w-5" />
                </span>
                <div>
                  <div className="font-bold text-foreground text-sm">Business Excellence</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Top honor for service quality & fleet support</div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {isOpen && typeof window !== 'undefined' && (
        <VideoModal
          channel="youtube"
          autoplay
          isOpen={isOpen}
          videoId="SKdPLZyIN0o"
          onClose={() => setOpen(false)}
        />
      )}
    </section>
  );
}

/* ---------------- Impact / CTA strip ---------------- */
function Impact() {
  return (
    <section className="px-4 pb-24 bg-background">
      <div className="mx-auto grid max-w-7xl items-stretch gap-5 md:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="overflow-hidden rounded-[1.75rem]"
        >
          <img
            src="/images/yacht-side.jpg"
            alt="Yacht on open water"
            loading="lazy"
            width={1000}
            height={700}
            className="h-full w-full object-cover"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="card-surface flex flex-col justify-between p-8 md:p-12"
        >
          <div>
            <span className="pill text-primary">IMPACT</span>
            <h3 className="mt-5 text-4xl font-black leading-tight md:text-5xl">
              TRACK, SECURE
              <br />
              <span className="text-primary">YOUR FLEET</span>
            </h3>
            <p className="mt-4 max-w-md text-muted-foreground">
              Monitor every vessel from one dashboard. Replay routes, set geofences, and receive
              alerts the moment anything moves off plan.
            </p>
          </div>
          <a
            href="/contact"
            className="mt-8 inline-flex w-fit items-center gap-2 rounded-full bg-foreground px-6 py-3 text-sm font-semibold text-background transition hover:bg-primary"
          >
            Request a demo <ArrowRight className="h-4 w-4" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}

const REVIEWS = [
  {
    name: 'Harbush Antoria',
    role: 'Fleet Manager',
    text: 'Missi Track gave us instant visibility on 40 vessels at once. Onboarding was smooth and support is unmatched in the region.',
    rating: 5,
  },
  {
    name: 'Linda Wijaya',
    role: 'Ops Director',
    text: 'Their Iridium Edge Solar units have been running for years without a single maintenance call. Set it and forget it.',
    rating: 5,
  },
  {
    name: 'Captain Reza',
    role: 'Master',
    text: 'Real-time RPM and route history finally on one screen. Made my reporting workflow ten times faster.',
    rating: 4,
  },
];

function Reviews() {
  return (
    <section className="px-4 pb-24 bg-background">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-10 flex items-end justify-between gap-4"
        >
          <h2 className="text-4xl font-black md:text-6xl">CLIENT REVIEWS</h2>
          <span className="hidden text-sm text-muted-foreground md:block">
            Trusted by 120+ maritime operators
          </span>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={stagger}
          className="grid gap-5 md:grid-cols-3"
        >
          {REVIEWS.map((r) => (
            <motion.figure
              key={r.name}
              variants={fadeUp}
              className="card-surface flex h-full flex-col p-6 hover-lift"
            >
              <div className="mb-4 flex gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className={`h-4 w-4 ${i < r.rating ? 'fill-primary text-primary' : 'text-muted-foreground/30'
                      }`}
                  />
                ))}
              </div>
              <blockquote className="flex-1 text-sm leading-relaxed">&quot;{r.text}&quot;</blockquote>
              <figcaption className="mt-6 border-t pt-4">
                <div className="font-semibold">{r.name}</div>
                <div className="text-xs text-muted-foreground">{r.role}</div>
              </figcaption>
            </motion.figure>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- CTA ---------------- */
function CTASection() {
  return (
    <section id="contact" className="px-4 pb-24 bg-background">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="relative mx-auto max-w-7xl overflow-hidden rounded-[2rem] p-10 text-white md:p-20"
        style={{ background: 'linear-gradient(135deg, #1e293b, #0f172a)' }}
      >
        <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-brand-500/30 blur-3xl" />
        <div className="relative grid items-center space-y-8 md:space-y-0 md:grid-cols-[1fr_auto]">
          <div>
            <span
              className="pill text-white shadow-none"
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
            >
              DEMO
            </span>
            <h2 className="mt-5 text-4xl font-black leading-tight md:text-6xl">
              Don&apos;t wait — experience
              <br />
              <span className="text-brand-500">our system</span> now
            </h2>
            <p className="mt-4 max-w-lg text-slate-300">
              Request a trial demo and see Missi Track integrate seamlessly with your internal systems
              in minutes.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <a
              href="/contact"
              className="group inline-flex items-center gap-3 rounded-full bg-brand-500 px-8 py-4 text-base font-semibold text-white shadow-lg transition hover:scale-105 hover:bg-brand-600"
            >
              <span>Request a demo</span>
              <ArrowRight className="h-5 w-5 transition group-hover:translate-x-1" />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.missi.app"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 rounded-full border border-white/25 bg-white/10 px-6 py-3.5 text-white shadow-lg backdrop-blur-md transition hover:scale-105 hover:bg-white/15"
            >
              <svg className="h-6 w-6 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M3.61 1.814L13.793 12 3.61 22.186a1.75 1.75 0 01-.473-1.206V3.02c0-.465.174-.895.473-1.206z" />
                <path fill="#34A853" d="M17.218 8.575L13.793 12 3.61 1.814c.385-.401.988-.52 1.547-.202l12.061 6.963z" />
                <path fill="#FBBC05" d="M20.892 10.696c.875.505.875 2.103 0 2.608l-3.674 2.121L13.793 12l3.425-3.425 3.674 2.121z" />
                <path fill="#EA4335" d="M17.218 15.425L5.157 22.388c-.559.318-1.162.199-1.547-.202L13.793 12l3.425 3.425z" />
              </svg>
              <div className="flex flex-col items-start leading-none text-left">
                <span className="text-[10px] font-medium uppercase tracking-wider text-slate-300">Get it on</span>
                <span className="text-sm font-bold tracking-tight text-white mt-0.5">Google Play</span>
              </div>
            </a>
          </div>
        </div>
      </motion.div>
    </section>
  );
}

Home.guestGuard = false;
Home.authGuard = false;

export default Home;

export const Route = createFileRoute('/')({
  component: Home,
});
